import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

export default function AddModal({ open, handleClose, handleSnack, onSuccess }) {
  const [formData, setFormData] = useState({
    site: "",
    nom: "",
    direction: "Entrée",
    statut: "Active",
  });
  
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  
  useEffect(() => {
    if (open) {
      fetchSites();
    }
  }, [open]);
  
  const fetchSites = async () => {
    try {
      const response = await api.get('/sites');
      setSites(response.data || []);
    } catch (error) {
      console.error('Error fetching sites:', error);
      handleSnack('Erreur lors du chargement des sites', 'error');
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    
    // Clear error for the field being changed
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear general error when any field changes
    if (generalError) {
      setGeneralError('');
    }
  };
  
  const resetForm = () => {
    setFormData({
      site: "",
      nom: "",
      direction: "Entrée",
      statut: "Active",
    });
    setErrors({});
    setGeneralError("");
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate required fields based on ShiftController.php rules
    if (!formData.nom) {
      newErrors.nom = 'Le nom est requis.';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async () => {
    // Clear previous errors
    setErrors({});
    setGeneralError("");
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const shiftData = {
        ID_Site: formData.site,
        name: formData.nom,
        Direction: formData.direction,
        Status: formData.statut
      };
      
      await api.post('/shifts', shiftData);
      handleSnack('Shift ajouté avec succès', 'success');
      handleClose();
      if (onSuccess) onSuccess();
      
      // Reset form
      resetForm();
    } catch (error) {
      console.error('Error adding shift:', error);
      
      if (error.response) {
        // Handle different error responses
        const { status, data } = error.response;
        
        if (status === 400 || status === 422) {
          // Validation errors
          if (data.name) {
            setErrors(prev => ({ ...prev, nom: data.name[0] }));
          }
          if (data.ID_Site) {
            setErrors(prev => ({ ...prev, site: data.ID_Site[0] }));
          }
          // Add other field-specific errors as needed
        } else if (status === 500) {
          // Server error
          setGeneralError("Erreur serveur. Veuillez réessayer plus tard.");
        } else {
          // Other errors
          setGeneralError("Une erreur s'est produite. Veuillez réessayer.");
        }
      } else {
        // Network error
        setGeneralError("Erreur de connexion. Veuillez vérifier votre connexion internet.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        AJOUTER UN SHIFT
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Typography variant="subtitle2">Site</Typography>
        <FormControl fullWidth sx={inputStyle} error={!!errors.site}>
          <Select name="site" value={formData.site} onChange={handleChange}>
            <MenuItem value="">Sélectionner un site</MenuItem>
            {sites.map(site => (
              <MenuItem key={site.ID_Site} value={site.ID_Site}>
                {site.name}
              </MenuItem>
            ))}
          </Select>
          {errors.site && <FormHelperText error>{errors.site}</FormHelperText>}
        </FormControl>
        <Typography variant="subtitle2">Nom</Typography>
        <TextField 
          fullWidth 
          name="nom" 
          value={formData.nom} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={!!errors.nom}
          helperText={errors.nom}
        />
        <Typography variant="subtitle2">Direction</Typography>
        <RadioGroup row name="direction" value={formData.direction} onChange={handleChange}>
          <FormControlLabel value="Entrée" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Entrée" />
          <FormControlLabel value="Sortie" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Sortie" />
        </RadioGroup>
        <Typography variant="subtitle2">Statut</Typography>
        <RadioGroup row name="statut" value={formData.statut} onChange={handleChange}>
          <FormControlLabel value="Active" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Active" />
          <FormControlLabel value="Inactive" control={<Radio sx={{ '&.Mui-checked': { color: '#000' } }} />} label="Inactive" />
        </RadioGroup>
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="outlined" 
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={<CheckIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
